import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database types
export interface ScorecardRecord {
 id: string;
 data: any;
 version: string;
 last_modified: string;
 user_id?: string;
}

export interface MetricUpdate {
 metric_id: string;
 value: string;
 target: string;
 last_updated: string;
 user_id?: string;
}

// Database service class
export class DatabaseService {
 // Save scorecard data
 static async saveScorecardData(data: any, version = '1.0') {
  const { data: result, error } = await supabase
   .from('scorecard_data')
   .upsert({
    id: 'main_scorecard',
    data,
    version,
    last_modified: new Date().toISOString()
   })
   .select()
   .single();

  if (error) throw error;
  return result;
 }

 // Load scorecard data
 static async loadScorecardData() {
  const { data, error } = await supabase
   .from('scorecard_data')
   .select('*')
   .eq('id', 'main_scorecard')
   .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
 }

 // Update individual metric
 static async updateMetric(metricId: string, value: string, target: string) {
  const { data, error } = await supabase
   .from('metric_updates')
   .upsert({
    metric_id: metricId,
    value,
    target,
    last_updated: new Date().toISOString()
   })
   .select()
   .single();

  if (error) throw error;
  return data;
 }

 // Get metric updates
 static async getMetricUpdates() {
  const { data, error } = await supabase
   .from('metric_updates')
   .select('*')
   .order('last_updated', { ascending: false });

  if (error) throw error;
  return data || [];
 }

 // Real-time subscription to scorecard changes
 static subscribeToScorecardChanges(callback: (payload: any) => void) {
  return supabase
   .channel('scorecard_changes')
   .on('postgres_changes', 
    { 
     event: '*', 
     schema: 'public', 
     table: 'scorecard_data' 
    }, 
    callback
   )
   .on('postgres_changes', 
    { 
     event: '*', 
     schema: 'public', 
     table: 'metric_updates' 
    }, 
    callback
   )
   .subscribe();
 }

 // Authentication methods
 static async signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
   email,
   password,
  });
  if (error) throw error;
  return data;
 }

 static async signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({
   email,
   password,
  });
  if (error) throw error;
  return data;
 }

 static async signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
 }

 static async getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
 }

 // Check if user is authenticated
 static async isAuthenticated() {
  const user = await this.getCurrentUser();
  return !!user;
 }

 // Save org chart data
 static async saveOrgChartData(data: any) {
  const { data: result, error } = await supabase
   .from('scorecard_data')
   .upsert({
    id: 'org_chart_data',
    data,
    version: '1.0',
    last_modified: new Date().toISOString()
   })
   .select()
   .single();

  if (error) throw error;
  return result;
 }

 // Load org chart data
 static async loadOrgChartData() {
  const { data, error } = await supabase
   .from('scorecard_data')
   .select('*')
   .eq('id', 'org_chart_data')
   .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
 }

 // Save gap analysis data
 static async saveGapData(data: any) {
  const { data: result, error } = await supabase
   .from('scorecard_data')
   .upsert({
    id: 'gap_analysis_data',
    data,
    version: '1.0',
    last_modified: new Date().toISOString()
   })
   .select()
   .single();

  if (error) throw error;
  return result;
 }

 // Load gap analysis data
 static async loadGapData() {
  const { data, error } = await supabase
   .from('scorecard_data')
   .select('*')
   .eq('id', 'gap_analysis_data')
   .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
 }
}